import { Suspense, type ComponentType, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '../components/ErrorBoundary.js';
import { Estado } from '../components/ui.js';
import { AvisoDeSincronizacion } from '../components/AvisoDeSincronizacion.js';
import { SyncIndicator } from '../components/SyncIndicator.js';
import {
  IconoAjustes,
  IconoEntrenar,
  IconoInicio,
  IconoPlan,
  IconoProgreso,
} from '../components/iconos.js';
import './shell.css';

/** Las cinco areas de navegacion (requisito §41). */
const AREAS: { to: string; etiqueta: string; Icono: ComponentType<{ tam?: number }> }[] = [
  { to: '/', etiqueta: 'Inicio', Icono: IconoInicio },
  { to: '/plan', etiqueta: 'Plan', Icono: IconoPlan },
  { to: '/entrenar', etiqueta: 'Entrenar', Icono: IconoEntrenar },
  { to: '/progreso', etiqueta: 'Progreso', Icono: IconoProgreso },
  { to: '/mas', etiqueta: 'Más', Icono: IconoAjustes },
];

/**
 * ¿Estamos dentro de una serie?
 *
 * El §2 dice que durante un entrenamiento activo la navegacion se oculta en
 * movil, y no estaba implementado: la barra se quedaba justo debajo de
 * «Guardar serie», que es donde cae el pulgar. Se mira la RUTA y no un estado
 * global porque esa es exactamente la condicion —estar en la pantalla de
 * registro—, y porque asi la barra vuelve sola al salir sin que nadie tenga
 * que acordarse de restaurarla.
 */
function enSesionActiva(pathname: string): boolean {
  return pathname.endsWith('/activa');
}

/**
 * El test inicial también va concentrado: una pregunta por pantalla, con el
 * «Siguiente» donde cae el pulgar. La barra de navegación debajo solo ofrecería
 * irse a mitad de una respuesta.
 */
function enTestInicial(pathname: string): boolean {
  return pathname === '/bienvenida';
}

export function AppShell(): ReactNode {
  const { pathname } = useLocation();
  const concentrado = enSesionActiva(pathname) || enTestInicial(pathname);

  return (
    <div className={`shell${concentrado ? ' shell--concentrado' : ''}`}>
      <nav className="nav-lateral" aria-label="Navegación principal">
        <span className="nav-lateral__marca">
          <span className="nav-lateral__pulso" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          Cadencia
        </span>
        {AREAS.map((area) => (
          <NavLink key={area.to} to={area.to} end={area.to === '/'} className="nav-lateral__enlace">
            <area.Icono tam={20} />
            {area.etiqueta}
          </NavLink>
        ))}
        <div className="nav-lateral__pie">
          <SyncIndicator />
        </div>
      </nav>

      <main className="shell__contenido">
        {/* En móvil el `SyncIndicator` de la barra lateral no existe —la barra
            es `display: none` por debajo de 1024 px—, así que el estado de la
            sincronización se pinta aquí. Fuera de la frontera de error: si la
            pantalla falla, saber si hay red sigue sirviendo. */}
        <AvisoDeSincronizacion concentrado={concentrado} />
        {/* Una frontera por ruta, y con `key`: si una pantalla falla, la
            navegacion sigue en pie y cambiar de pestaña la reinicia en vez de
            dejar al usuario atrapado en el error.

            Y cubre tambien las rutas diferidas (PERF-002): si el trozo de una
            pantalla no se puede descargar, `React.lazy` lo propaga como un
            error de render y lo recoge esta misma frontera. Una navegacion
            diferida no se convierte en una pantalla muda, y no ha hecho falta
            un mecanismo nuevo.

            El `Suspense` va DENTRO, no envolviendo al `AppShell`: mientras
            llega el trozo se sustituye el contenido y las dos barras de
            navegacion se quedan donde estaban. Fuera, el esqueleto se comeria
            la pantalla entera y cada navegacion daria un salto. */}
        <ErrorBoundary key={pathname} ambito={`ruta ${pathname}`}>
          <Suspense fallback={<Estado tipo="cargando" titulo="Abriendo la pantalla" />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>

      <nav className="nav-inferior" aria-label="Navegación principal">
        {AREAS.map((area) => (
          <NavLink
            key={area.to}
            to={area.to}
            end={area.to === '/'}
            className="nav-inferior__enlace"
          >
            <area.Icono tam={22} />
            {area.etiqueta}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
